import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import tdcLogo from '../../assets/logo-transparent-tdc-only.png';
import { useUser } from '../../hooks/useUser';
import { isAdmin, isInternal } from '../../utils';
import { useNoIndexMeta } from '../../hooks/useNoIndexMeta';
import { formatResponseDate } from '../videoProduction/helper';
import { ORDER_QUESTIONS, PROJECT_QUESTIONS } from './constants';
import { KnownIdentity, useOrderFeedback } from './useOrderFeedback';
import { Stepper } from './Stepper';
import { QuestionList } from './QuestionList';
import { ProjectStep } from './ProjectStep';
import { SubmitStep } from './SubmitStep';
import { FooterBar } from './FooterBar';
import { ScoreSummarySection } from './ScoreSummarySection';

const PAGE = 'bg-page min-h-screen pb-[92px]';
const PAGE_NO_FOOTER = 'bg-page min-h-screen pb-10';
const CENTERED = 'max-w-[680px] mx-auto px-5 py-16 text-center';
const TITLE = 'text-[26px] font-extrabold text-text-1 tracking-[-0.4px] m-0';
const SUB = 'text-[13px] text-text-2 mt-[5px]';
const LOGO = 'w-[122px] mx-auto mb-5 block';

const Notice = ({ title, body }: { title: string; body: string }) => (
    <div className={PAGE}>
        <div className={CENTERED}>
            <img className={LOGO} alt="The Desire Company" src={tdcLogo} />
            <h1 className={TITLE}>{title}</h1>
            <p className={SUB}>{body}</p>
        </div>
    </div>
);

export default function OrderFeedbackPage() {
    useNoIndexMeta();

    const { token } = useParams<{ token: string }>();
    const { user, role } = useUser();

    const readOnly = isInternal(role) && !isAdmin(role);

    const identity = useMemo<KnownIdentity | null>(() => {
        if (!user) return null;
        const email = typeof user.emailAddress === 'string' ? user.emailAddress : '';
        return { name: user.displayName || '', email };
    }, [user]);

    const feedback = useOrderFeedback(token, identity);
    const {
        form,
        loading,
        saving,
        invalid,
        step,
        setStep,
        steps,
        stepDone,
        progress,
    } = feedback;

    if (loading) {
        return (
            <div className={CENTERED}>
                <CircularProgress />
            </div>
        );
    }

    if (invalid || !form) {
        return (
            <Notice
                title="This feedback link is no longer valid"
                body="Please get in touch with your account manager for a new link."
            />
        );
    }

    const current = steps[step];
    const project =
        current.kind === 'project'
            ? form.projects[current.projectIndex as number]
            : null;

    return (
        <div className={readOnly ? PAGE_NO_FOOTER : PAGE}>
            <div className="max-w-[680px] mx-auto px-5 pt-7 pb-5">
                <div className="text-center mb-[22px]">
                    {user ? (
                        <Link to="/" className={LOGO} aria-label="Back to the portal">
                            <img className="size-full" alt="The Desire Company" src={tdcLogo} />
                        </Link>
                    ) : (
                        <img className={LOGO} alt="The Desire Company" src={tdcLogo} />
                    )}
                    <h1 className={TITLE}>Your Feedback</h1>
                    <p className={SUB}>
                        {form.order.brand ? `${form.order.brand.name} · ` : ''}
                        {form.order.name}
                    </p>
                </div>

                {readOnly && (
                    <div className="mb-[18px] px-4 py-3 rounded-xl border border-[#F5D9A8] bg-[#FDF2E3] text-[13px] text-[#9A5B00] leading-[1.5] dark:border-border-token dark:bg-[#7A5B12]/30 dark:text-[#E0BE73]">
                        {form.submitted
                            ? "The client submitted this feedback. It is read only for your role."
                            : "The client has not submitted this yet. It is read only for your role."}
                    </div>
                )}

                {!readOnly && (
                    <Stepper
                        steps={steps}
                        current={step}
                        done={stepDone}
                        onSelect={setStep}
                    />
                )}

                {readOnly ? (
                    <div className="flex flex-col gap-[18px]">
                        {form.submitted && (
                            <div className="bg-surface border border-border-token rounded-2xl px-[26px] py-5 max-[560px]:px-[18px] flex flex-wrap gap-x-8 gap-y-2">
                                <div>
                                    <span className="block text-[11.5px] text-muted">Submitted by</span>
                                    <span className="block text-[13.5px] font-semibold text-text-1">
                                        {form.submitted_by_name || '—'}
                                    </span>
                                </div>
                                <div>
                                    <span className="block text-[11.5px] text-muted">Email</span>
                                    <span className="block text-[13.5px] font-semibold text-text-1 break-all">
                                        {form.submitted_by_email || '—'}
                                    </span>
                                </div>
                                <div>
                                    <span className="block text-[11.5px] text-muted">Submitted</span>
                                    <span className="block text-[13.5px] font-semibold text-text-1">
                                        {formatResponseDate(form.submitted_at) || '—'}
                                    </span>
                                </div>
                            </div>
                        )}

                        <ScoreSummarySection
                            eyebrow="The Order"
                            title="Overall experience"
                            scores={ORDER_QUESTIONS.map(
                                (q) => feedback.orderScores[q.field] ?? null,
                            )}
                            defaultOpen
                        >
                            <QuestionList
                                questions={ORDER_QUESTIONS}
                                scores={feedback.orderScores}
                                commentLabel="Additional comments on the order"
                                commentPlaceholder=""
                                comment={feedback.orderComment}
                                onScore={feedback.setOrderScore}
                                onComment={() => undefined}
                                readOnly
                            />
                        </ScoreSummarySection>

                        {form.projects.map((row, index) => (
                            <ScoreSummarySection
                                key={row.id}
                                eyebrow={`Project ${index + 1} of ${form.projects.length}${
                                    row.content_type ? ` · ${row.content_type}` : ''
                                }`}
                                title={row.title}
                                scores={PROJECT_QUESTIONS.map(
                                    (q) =>
                                        (feedback.projectScores[row.id] || {})[q.field] ??
                                        null,
                                )}
                            >
                                <ProjectStep
                                    project={row}
                                    position={index + 1}
                                    total={form.projects.length}
                                    scores={feedback.projectScores[row.id] || {}}
                                    comment={feedback.projectComment(row.id)}
                                    entityScore={feedback.entityScore}
                                    onScore={() => undefined}
                                    onComment={() => undefined}
                                    onEntityScore={() => undefined}
                                    readOnly
                                />
                            </ScoreSummarySection>
                        ))}
                    </div>
                ) : (
                <div className="bg-surface border border-border-token rounded-2xl p-[26px] max-[560px]:p-[18px]">
                    {current.kind === 'order' && (
                        <>
                            <div className="pb-5 mb-[22px] border-b border-border-token">
                                {/* Section-identity chip: the purple pair has no
                                    token role (the sibling steps use their own
                                    hues), so it stays literal. */}
                                <span className="inline-flex items-center gap-[5px] text-[11px] font-bold px-2.5 py-[3px] rounded-md uppercase tracking-[0.4px] text-[#3C3489] bg-[#EEEDFE]">
                                    The Order
                                </span>
                                <h2 className="text-[20px] font-bold text-text-1 mt-2.5 mb-0 tracking-[-0.2px]">
                                    Your experience
                                </h2>
                                <p className="text-[13px] text-text-2 mt-1">
                                    How was the overall engagement with The Desire
                                    Company?
                                </p>
                            </div>
                            <QuestionList
                                questions={ORDER_QUESTIONS}
                                scores={feedback.orderScores}
                                commentLabel="Additional comments on the order"
                                commentPlaceholder="Anything about the overall engagement…"
                                comment={feedback.orderComment}
                                onScore={feedback.setOrderScore}
                                onComment={(value) => feedback.setComment('order', value)}
                                readOnly={readOnly}
                            />
                        </>
                    )}

                    {project && (
                        <ProjectStep
                            project={project}
                            position={(current.projectIndex as number) + 1}
                            total={form.projects.length}
                            scores={feedback.projectScores[project.id] || {}}
                            comment={feedback.projectComment(project.id)}
                            entityScore={feedback.entityScore}
                            onScore={(field, value) =>
                                feedback.setProjectScore(project.id, field, value)
                            }
                            onComment={(value) =>
                                feedback.setComment(
                                    feedback.projectCommentKey(project.id),
                                    value,
                                )
                            }
                            onEntityScore={feedback.setEntityScore}
                            readOnly={readOnly}
                        />
                    )}

                    {current.kind === 'submit' && (
                        <SubmitStep
                            form={form}
                            steps={steps}
                            stepDone={stepDone}
                            name={feedback.name}
                            email={feedback.email}
                            askForContact={!identity}
                            onSelectStep={setStep}
                            onName={feedback.setName}
                            onEmail={feedback.setEmail}
                        />
                    )}
                </div>
                )}
            </div>

            {!readOnly && (
                <FooterBar
                    progress={progress}
                    saving={saving}
                    canGoBack={step > 0}
                    isLastStep={current.kind === 'submit'}
                    alreadySubmitted={form.submitted}
                    onSave={feedback.save}
                    onBack={() => setStep(step - 1)}
                    onNext={() => setStep(step + 1)}
                    onSubmit={feedback.submit}
                    readOnly={readOnly}
                />
            )}

        </div>
    );
}
